import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { notificationService, type Notification } from '../services/notificationService';

interface NotificationContainerProps {
    className?: string;
}

export default function NotificationContainer({ className = '' }: NotificationContainerProps) {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const location = useLocation();
    const isSupportPage = location.pathname.startsWith('/support');

    useEffect(() => {
        const unsubscribe = notificationService.subscribe(setNotifications);
        return unsubscribe;
    }, []);

    const getIcon = (type: string) => {
        // If on support page, use appropriate colors for icons
        if (isSupportPage) {
            switch (type) {
                case 'success':
                    return <CheckCircle className="w-5 h-5 text-green-600" />;
                case 'error':
                    return <AlertCircle className="w-5 h-5 text-red-600" />;
                case 'warning':
                    return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
                case 'info':
                    return <Info className="w-5 h-5 text-blue-600" />;
                default:
                    return <Info className="w-5 h-5 text-gray-600" />;
            }
        }
        
        // Default icon colors for other pages
        switch (type) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-400" />;
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-400" />;
            case 'warning':
                return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
            case 'info':
                return <Info className="w-5 h-5 text-blue-400" />;
            default:
                return <Info className="w-5 h-5 text-gray-400" />;
        }
    };

    const getBackgroundColor = (type: string) => {
        // If on support page, use solid background colors instead of transparent
        if (isSupportPage) {
            switch (type) {
                case 'success':
                    return 'bg-green-50 border-green-300';
                case 'error':
                    return 'bg-red-50 border-red-300';
                case 'warning':
                    return 'bg-yellow-50 border-yellow-300';
                case 'info':
                    return 'bg-blue-50 border-blue-300';
                default:
                    return 'bg-gray-50 border-gray-300';
            }
        }
        
        // Default transparent style for other pages
        switch (type) {
            case 'success':
                return 'bg-green-500/20 border-green-400/50 backdrop-blur-md';
            case 'error':
                return 'bg-red-500/20 border-red-400/50 backdrop-blur-md';
            case 'warning':
                return 'bg-yellow-500/20 border-yellow-400/50 backdrop-blur-md';
            case 'info':
                return 'bg-blue-500/20 border-blue-400/50 backdrop-blur-md';
            default:
                return 'bg-gray-500/20 border-gray-400/50 backdrop-blur-md';
        }
    };

    const getTextColor = (type: string) => {
        // If on support page, use dark text for contrast against light background
        if (isSupportPage) {
            switch (type) {
                case 'success':
                    return 'text-green-800';
                case 'error':
                    return 'text-red-800';
                case 'warning':
                    return 'text-yellow-800';
                case 'info':
                    return 'text-blue-800';
                default:
                    return 'text-gray-800';
            }
        }
        
        // Default text colors for other pages
        switch (type) {
            case 'success':
                return 'text-green-200';
            case 'error':
                return 'text-red-200';
            case 'warning':
                return 'text-yellow-200';
            case 'info':
                return 'text-blue-200';
            default:
                return 'text-gray-200';
        }
    };

    const handleDismiss = (id: string) => {
        notificationService.dismiss(id);
    };

    const handleAction = (action: () => void) => {
        action();
    };

    if (notifications.length === 0) {
        return null;
    }

    return (
        <div 
            className={`fixed top-4 right-4 z-50 space-y-3 w-full ${className}`}
            style={{ maxWidth: 'min(28rem, calc(100vw - 2rem))' }}
        >
            {notifications.map((notification) => (
                <div
                    key={notification.id}
                    className={`
                        transform transition-all duration-300 ease-in-out
                        ${notification.dismissed 
                            ? 'translate-x-full opacity-0 scale-95' 
                            : 'translate-x-0 opacity-100 scale-100'
                        }
                        ${getBackgroundColor(notification.type)}
                        border rounded-lg shadow-lg p-4
                        w-full
                    `}
                >
                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            {getIcon(notification.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0 overflow-hidden">
                            {notification.title && (
                                <h4 className={`text-sm font-semibold ${getTextColor(notification.type)} mb-1 break-words`}>
                                    {notification.title}
                                </h4>
                            )}
                            
                            <p className={`text-sm ${getTextColor(notification.type)} break-words`}>
                                {notification.message && notification.message.trim() 
                                    ? notification.message.trim() 
                                    : (notification.type === 'error' ? 'An error occurred' : 'Notification')}
                            </p>

                            {notification.actions && notification.actions.length > 0 && (
                                <div className="mt-3 flex space-x-2">
                                    {notification.actions.map((action, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleAction(action.action)}
                                            className={`
                                                px-3 py-1 text-xs font-medium rounded-md transition-colors
                                                ${action.style === 'primary' 
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                    : action.style === 'danger'
                                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                                }
                                            `}
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => handleDismiss(notification.id)}
                            className={`
                                flex-shrink-0 p-1 rounded-full transition-colors
                                ${isSupportPage 
                                    ? 'text-gray-500 hover:bg-gray-200' 
                                    : `${getTextColor(notification.type)} hover:bg-white/10`
                                }
                            `}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
